import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gem, Mountain, TreePine, LayoutPanelTop } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { materials } from "@/content/site";
import { Reveal, SectionHead } from "@/components/apex/primitives";

const ICONS = { porcelain: Gem, stone: Mountain, wood: TreePine, panels: LayoutPanelTop };

const Materials = () => {
  const { t, isRtl } = useLang();
  const [activeId, setActiveId] = useState(materials[0].id);
  const active = materials.find((m) => m.id === activeId);

  return (
    <section data-testid="materials-section" className="relative bg-[var(--cream-2)] py-14 sm:py-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead label={t.materials.label} title={t.materials.title} body={t.materials.body} />

        <div className="mt-10 grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
          <Reveal>
            <ul className="grid gap-4 sm:grid-cols-2">
              {materials.map((m) => {
                const on = m.id === activeId;
                const Icon = ICONS[m.id] || Gem;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(m.id)}
                      aria-pressed={on}
                      data-testid={`material-swatch-${m.id}`}
                      className={`group flex h-full w-full flex-col items-start gap-5 rounded-[var(--r)] p-6 text-start transition-all duration-500 ${
                        on
                          ? "bg-[var(--charcoal)] text-white shadow-[0_30px_50px_-30px_rgba(21,21,21,0.5)]"
                          : "bg-white text-[var(--text)] hover:-translate-y-1.5 hover:shadow-[0_24px_40px_-30px_rgba(21,21,21,0.3)]"
                      }`}
                    >
                      <span className="ap-icon">
                        <Icon className="h-6 w-6" strokeWidth={1.7} />
                      </span>
                      <span>
                        <span className="ap-display block text-xl">{isRtl ? m.name_ar : m.name_en}</span>
                        <span className={`mt-2 block text-[13.5px] leading-[1.8] ${on ? "text-white/65" : "text-[var(--muted)]"}`}>
                          {isRtl ? m.desc_ar : m.desc_en}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <p className="mt-6 text-[13.5px] leading-relaxed text-[var(--muted)]">{t.materials.note}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative" data-testid="material-preview" style={{ aspectRatio: "4 / 5" }}>
              <AnimatePresence mode="wait">
                {active.image ? (
                  <motion.div
                    key={active.id}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="ap-img absolute inset-0"
                  >
                    <img
                      src={active.image}
                      alt={`${isRtl ? active.name_ar : active.name_en} — ${t.materials.realTag}`}
                      className="absolute inset-0 h-full w-full object-cover"
                      style={{ objectPosition: active.focus }}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key={active.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0 grid place-items-center rounded-[var(--r)] border-2 border-dashed border-[var(--muted-2)]/50 bg-white p-10 text-center"
                  >
                    <div className="max-w-sm">
                      <p className="ap-display text-2xl text-[var(--text)]">{isRtl ? active.name_ar : active.name_en}</p>
                      <p className="mt-3 text-[14px] leading-relaxed text-[var(--muted)]">
                        {isRtl ? active.desc_ar : active.desc_en}
                      </p>
                      <p className="mt-6 text-[12.5px] leading-relaxed text-[var(--muted-2)]">{t.materials.sampleHint}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
              <span
                data-testid="material-preview-tag"
                className="absolute bottom-5 start-5 z-10 rounded-full bg-white px-4 py-2 text-[12.5px] font-semibold text-[var(--text)] shadow-lg"
              >
                {active.real ? t.materials.realTag : t.materials.sampleTag}
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Materials;
